// ─────────────────────────────────────────────────────────────────────────────
// CRITICAL THINKING QUESTION BANK — 40 items · 5 dimensions · 8 items each
// Science: Watson-Glaser (1980) CT framework · Facione (1990) Delphi Report
//          Ennis (1985) CT skills taxonomy · Open-domain constructs
// Context: Indian professional settings — consulting, legal, finance, management
// Format:  Passage-based · Custom options per dimension
// ─────────────────────────────────────────────────────────────────────────────

// DIMENSION 1 — INFERENCE (8 items)
// Given a passage of facts, determine whether the statement is:
// True · False · Cannot Say
export const INFERENCE_ITEMS = [
  {
    id:"ct01", dim:"INF", section:"Inference",
    passage:"A Bengaluru-based IT services company reported 22% revenue growth in FY2025. Its headcount increased by 8% over the same period. The company's attrition rate fell from 19% to 13% year-on-year. Three new delivery centres were opened in Pune, Hyderabad and Chennai.",
    q:"The company's revenue per employee increased between FY2024 and FY2025.",
    opts:["True","False","Cannot Say"], ans:0,
    exp:"Revenue grew 22% while headcount grew only 8%, so revenue per employee must have risen."
  },
  {
    id:"ct02", dim:"INF", section:"Inference",
    passage:"A leading NBFC in India disbursed ₹4,800 crore in retail loans in Q3 FY2025, compared to ₹3,600 crore in Q3 FY2024. Its gross NPA ratio improved from 3.8% to 3.1% over the same period. The RBI repo rate remained unchanged at 6.5% throughout this period.",
    q:"The NBFC's absolute gross NPA amount decreased between Q3 FY2024 and Q3 FY2025.",
    opts:["True","False","Cannot Say"], ans:2,
    exp:"NPA ratio improved but loan book grew significantly — total NPAs could have risen or fallen. Cannot be determined."
  },
  {
    id:"ct03", dim:"INF", section:"Inference",
    passage:"A pharmaceutical company's field force covers 450 territories across India. The top 20% of Medical Representatives (MRs) contribute 58% of total sales. The bottom 20% contribute 7% of sales. Average MR tenure is 3.4 years.",
    q:"Replacing the bottom 20% of MRs would likely reduce total sales in the short term.",
    opts:["True","False","Cannot Say"], ans:0,
    exp:"Even low performers contribute 7% of sales. Replacing them creates transition gaps that reduce short-term sales."
  },
  {
    id:"ct04", dim:"INF", section:"Inference",
    passage:"A retail chain operates 340 stores across Tier-1 and Tier-2 cities. Walk-in traffic fell 12% in FY2025 while online order volume grew 34%. Total revenue increased by 6% year-on-year. The average online basket size is ₹1,850 versus ₹2,200 for in-store purchases.",
    q:"The number of total transactions (in-store + online) increased in FY2025.",
    opts:["True","False","Cannot Say"], ans:2,
    exp:"Revenue increased 6% but basket sizes differ and the mix shifted. Total transaction count cannot be determined without absolute numbers."
  },
  {
    id:"ct05", dim:"INF", section:"Inference",
    passage:"A management consulting firm surveyed 500 CHROs across India. 68% reported that structured psychometric assessments improved quality of hire. 44% use assessments for all roles, while 39% use them only for senior positions. Firms with over 10,000 employees are twice as likely to use assessments as those with fewer than 500 employees.",
    q:"Most large Indian firms (10,000+ employees) use psychometric assessments in their hiring process.",
    opts:["True","False","Cannot Say"], ans:0,
    exp:"Large firms are twice as likely as small firms to use assessments; given the general adoption rate (~44-68%), most large firms likely do."
  },
  {
    id:"ct06", dim:"INF", section:"Inference",
    passage:"A private sector bank recorded a 14% increase in branch walk-ins for loan inquiries in Q2. Of these inquiries, 31% converted to loan applications. The bank's digital loan applications grew 67% in the same quarter. Total loan disbursals grew 28% year-on-year.",
    q:"Digital channels contributed more to the bank's loan disbursals than branch channels in Q2.",
    opts:["True","False","Cannot Say"], ans:2,
    exp:"We know digital applications grew faster but not absolute volumes or disbursal rates per channel."
  },
  {
    id:"ct07", dim:"INF", section:"Inference",
    passage:"A Supreme Court judgment in 2024 held that mandatory arbitration clauses in standard-form consumer contracts are unenforceable if the consumer had no meaningful opportunity to negotiate the terms. The judgment cited the Consumer Protection Act, 2019 and principles of unconscionability.",
    q:"All arbitration clauses in consumer contracts in India are now unenforceable.",
    opts:["True","False","Cannot Say"], ans:1,
    exp:"The judgment applies specifically to mandatory clauses in standard-form contracts with no negotiation opportunity — not all arbitration clauses."
  },
  {
    id:"ct08", dim:"INF", section:"Inference",
    passage:"An FMCG company's rural distribution covers 4.2 million outlets. In FY2025, it added 480,000 rural outlets through its 'Direct to Village' initiative. Rural revenue grew 18%, faster than urban growth of 11%. The rural segment now accounts for 39% of total revenue.",
    q:"Prior to FY2025, the rural segment accounted for less than 39% of total revenue.",
    opts:["True","False","Cannot Say"], ans:0,
    exp:"If rural grew 18% and urban 11%, but rural was 39% at end of FY2025, the rural share must have been lower at the start."
  },
];

// DIMENSION 2 — RECOGNITION OF ASSUMPTIONS (8 items)
// A statement is given. Determine whether the proposed assumption is:
// Made (unstated but required) · Not Made
export const ASSUMPTION_ITEMS = [
  {
    id:"ct09", dim:"ASM", section:"Assumptions",
    passage:"Statement: 'We should hire candidates from the top 10 IIMs to strengthen our strategy consulting practice.'",
    q:"Assumption: Candidates from top 10 IIMs are more capable strategy consultants than those from other institutions.",
    opts:["Assumption Made","Not Made"], ans:0,
    exp:"The recommendation only makes sense if IIM candidates are assumed superior for this role."
  },
  {
    id:"ct10", dim:"ASM", section:"Assumptions",
    passage:"Statement: 'The company should expand to Southeast Asia before entering European markets.'",
    q:"Assumption: European markets require more regulatory compliance than Southeast Asian markets.",
    opts:["Assumption Made","Not Made"], ans:1,
    exp:"The priority could be based on market size, cultural proximity, cost, or competition — not necessarily regulatory complexity."
  },
  {
    id:"ct11", dim:"ASM", section:"Assumptions",
    passage:"Statement: 'Implementing a 4-day work week will improve employee productivity at our firm.'",
    q:"Assumption: Employees currently do not work at maximum productive capacity on all 5 days.",
    opts:["Assumption Made","Not Made"], ans:0,
    exp:"If employees were already fully productive every day, reducing working days would harm output. The statement assumes unused capacity exists."
  },
  {
    id:"ct12", dim:"ASM", section:"Assumptions",
    passage:"Statement: 'Candidates who score above the 70th percentile on the numerical reasoning test should be shortlisted for the analyst role.'",
    q:"Assumption: The 70th percentile cut-off has been validated against analyst job performance data.",
    opts:["Assumption Made","Not Made"], ans:1,
    exp:"A cut-off score can be set arbitrarily or by convention without requiring prior validation evidence."
  },
  {
    id:"ct13", dim:"ASM", section:"Assumptions",
    passage:"Statement: 'To reduce carbon emissions, the government should incentivise electric vehicles rather than banning petrol vehicles.'",
    q:"Assumption: Banning petrol vehicles would be more disruptive to the economy than providing EV incentives.",
    opts:["Assumption Made","Not Made"], ans:0,
    exp:"The preference for incentives over bans implies the latter would cause greater disruption — otherwise both options would be equally preferable."
  },
  {
    id:"ct14", dim:"ASM", section:"Assumptions",
    passage:"Statement: 'Investors should consider shifting a portion of their fixed deposits to equity mutual funds given current market conditions.'",
    q:"Assumption: The investor has a sufficient risk appetite to invest in equity markets.",
    opts:["Assumption Made","Not Made"], ans:0,
    exp:"Advising a shift to equity without qualifying risk appetite assumes the investor can tolerate equity market volatility."
  },
  {
    id:"ct15", dim:"ASM", section:"Assumptions",
    passage:"Statement: 'Legal firms should adopt AI-assisted contract review tools to improve turnaround times.'",
    q:"Assumption: Current contract review turnaround times are considered unacceptably slow by clients.",
    opts:["Assumption Made","Not Made"], ans:1,
    exp:"The recommendation is for improvement — not necessarily because current times are unacceptable. AI adoption could be a competitive advantage even if clients are satisfied."
  },
  {
    id:"ct16", dim:"ASM", section:"Assumptions",
    passage:"Statement: 'The merger between Company A and Company B will create the largest FMCG firm in South Asia by revenue.'",
    q:"Assumption: No other FMCG merger or acquisition will occur in South Asia before the deal closes.",
    opts:["Assumption Made","Not Made"], ans:0,
    exp:"If another combination creates a larger entity before this deal closes, the statement would be false. It implicitly assumes the competitive landscape remains stable."
  },
];

// DIMENSION 3 — DEDUCTION (8 items)
// Given premises, does the conclusion follow necessarily?
// Follows · Does Not Follow
export const DEDUCTION_ITEMS = [
  {
    id:"ct17", dim:"DED", section:"Deduction",
    passage:"All investment-grade bonds have a credit rating of BBB or higher. The Infra Bond Series IV has a credit rating of AA+.",
    q:"The Infra Bond Series IV is an investment-grade bond.",
    opts:["Conclusion Follows","Does Not Follow"], ans:0,
    exp:"AA+ is above BBB, so by the given definition, it qualifies as investment-grade."
  },
  {
    id:"ct18", dim:"DED", section:"Deduction",
    passage:"Employees who complete the leadership development programme are eligible for promotion to Senior Manager. Ravi completed the leadership development programme.",
    q:"Ravi will be promoted to Senior Manager.",
    opts:["Conclusion Follows","Does Not Follow"], ans:1,
    exp:"Completion makes Ravi eligible — not guaranteed to be promoted. Eligibility and promotion are different outcomes."
  },
  {
    id:"ct19", dim:"DED", section:"Deduction",
    passage:"No company found guilty of market manipulation under SEBI regulations can be listed on the NSE for five years. TechFin Ltd was found guilty of market manipulation by SEBI last year.",
    q:"TechFin Ltd cannot be listed on the NSE this year.",
    opts:["Conclusion Follows","Does Not Follow"], ans:0,
    exp:"The rule is absolute — no such company can be listed for five years. Last year falls within the five-year window."
  },
  {
    id:"ct20", dim:"DED", section:"Deduction",
    passage:"Some high-performing sales executives prefer incentive-based compensation over fixed pay. All sales executives at GlobalPharma have fixed-pay-only contracts.",
    q:"No GlobalPharma sales executive is a high performer.",
    opts:["Conclusion Follows","Does Not Follow"], ans:1,
    exp:"Some high performers prefer incentive pay — not all. High performers can still exist under fixed pay."
  },
  {
    id:"ct21", dim:"DED", section:"Deduction",
    passage:"Only those candidates with at least 5 years of direct tax experience will be considered for the Tax Director role. Priya has 7 years of experience, all in indirect taxation.",
    q:"Priya will not be considered for the Tax Director role.",
    opts:["Conclusion Follows","Does Not Follow"], ans:0,
    exp:"The requirement is 'direct tax experience.' Priya's experience is entirely indirect tax, so she does not qualify."
  },
  {
    id:"ct22", dim:"DED", section:"Deduction",
    passage:"Consulting projects that exceed 120 days require Partner-level sign-off. Projects below ₹50 lakh require only Associate Director sign-off regardless of duration.",
    q:"A 150-day project worth ₹40 lakh requires Partner-level sign-off.",
    opts:["Conclusion Follows","Does Not Follow"], ans:1,
    exp:"The second rule overrides based on value — projects below ₹50L require only AD sign-off regardless of duration."
  },
  {
    id:"ct23", dim:"DED", section:"Deduction",
    passage:"All contracts exceeding ₹10 crore must be reviewed by the Legal department before execution. The vendor agreement for the new ERP system is valued at ₹14.5 crore.",
    q:"The ERP vendor agreement must be reviewed by the Legal department.",
    opts:["Conclusion Follows","Does Not Follow"], ans:0,
    exp:"₹14.5 crore exceeds the ₹10 crore threshold — Legal review is mandatory."
  },
  {
    id:"ct24", dim:"DED", section:"Deduction",
    passage:"M&A transactions involving more than 30% acquisition of a listed Indian company require an open offer under SEBI Takeover Regulations. Firm X acquired a 28% stake in Horizon Ltd, a BSE-listed company.",
    q:"Firm X is required to make an open offer for Horizon Ltd.",
    opts:["Conclusion Follows","Does Not Follow"], ans:1,
    exp:"28% is below the 30% trigger threshold — no mandatory open offer is required."
  },
];

// DIMENSION 4 — INTERPRETATION (8 items)
// Given data/evidence, does the proposed conclusion follow?
// Follows · Does Not Follow
export const INTERPRETATION_ITEMS = [
  {
    id:"ct25", dim:"INT", section:"Interpretation",
    passage:"A study of 1,200 Indian manufacturing firms found that companies offering structured onboarding programmes had 23% lower first-year attrition than those without. The study controlled for industry, firm size and compensation levels.",
    q:"Implementing a structured onboarding programme is likely to reduce first-year attrition.",
    opts:["Conclusion Follows","Does Not Follow"], ans:0,
    exp:"The controlled study shows a causal-direction relationship between onboarding and retention. The conclusion is supported."
  },
  {
    id:"ct26", dim:"INT", section:"Interpretation",
    passage:"A survey of 600 retail banking customers found that 74% preferred mobile app banking for routine transactions. 61% said they still preferred visiting a branch for complex needs such as loans and investments.",
    q:"Banks should close branches and shift all operations to digital channels.",
    opts:["Conclusion Follows","Does Not Follow"], ans:1,
    exp:"The data shows branches remain preferred for complex needs. Closing all branches would conflict with customer preference."
  },
  {
    id:"ct27", dim:"INT", section:"Interpretation",
    passage:"City X increased traffic fines by 300% in 2023. Traffic fatalities in City X fell by 18% in 2023 compared to 2022. No other significant traffic safety interventions were implemented during this period.",
    q:"The increase in traffic fines likely contributed to the reduction in fatalities in City X.",
    opts:["Conclusion Follows","Does Not Follow"], ans:0,
    exp:"The timing, the magnitude of both changes, and the absence of other interventions support a causal attribution."
  },
  {
    id:"ct28", dim:"INT", section:"Interpretation",
    passage:"Company A and Company B operate in the same industry. Company A's employee satisfaction score is 7.8/10 while Company B's is 6.2/10. Company A's revenue per employee is ₹48 lakh versus Company B's ₹31 lakh.",
    q:"Higher employee satisfaction directly causes higher revenue per employee.",
    opts:["Conclusion Follows","Does Not Follow"], ans:1,
    exp:"Correlation is shown, but causation cannot be established. Other factors (technology, talent quality, pricing) could explain the revenue difference."
  },
  {
    id:"ct29", dim:"INT", section:"Interpretation",
    passage:"In a clinical trial of Drug X on 2,400 diabetic patients, 68% of the treatment group achieved target blood glucose levels compared to 41% in the placebo group. The difference was statistically significant (p<0.001). Adverse effects were reported in 8% of the treatment group.",
    q:"Drug X is effective in helping diabetic patients achieve target blood glucose levels.",
    opts:["Conclusion Follows","Does Not Follow"], ans:0,
    exp:"Large sample, controlled trial, statistically significant result. The evidence supports effectiveness, though real-world use must consider the 8% adverse effect rate."
  },
  {
    id:"ct30", dim:"INT", section:"Interpretation",
    passage:"A law firm analysed 800 commercial disputes resolved through arbitration versus litigation in India. Median resolution time was 14 months for arbitration and 58 months for litigation. Median legal costs were 40% lower in arbitration.",
    q:"Commercial parties in India should always choose arbitration over litigation.",
    opts:["Conclusion Follows","Does Not Follow"], ans:1,
    exp:"'Always' is too absolute. Certain disputes (e.g., those requiring precedent, injunctions, or third-party joinder) may be better suited for litigation despite higher cost and time."
  },
  {
    id:"ct31", dim:"INT", section:"Interpretation",
    passage:"A BCG study found that companies in the top quartile for gender diversity at the executive level are 21% more likely to achieve above-average profitability than industry peers. The study covered 1,000 firms across 15 countries.",
    q:"Increasing gender diversity in executive teams improves company profitability.",
    opts:["Conclusion Follows","Does Not Follow"], ans:1,
    exp:"The study shows correlation across a large sample but does not establish causation. Diverse firms may differ in other characteristics that drive profitability."
  },
  {
    id:"ct32", dim:"INT", section:"Interpretation",
    passage:"An analysis of 5,000 loan applications found that applicants with credit scores above 750 had a 2.1% default rate, while those with scores below 650 had a 14.8% default rate. Scores between 650-750 showed a 6.3% default rate.",
    q:"Credit score above 750 is a useful predictor of loan repayment behaviour.",
    opts:["Conclusion Follows","Does Not Follow"], ans:0,
    exp:"The data shows a strong and consistent relationship between score bands and default rates. Credit score is clearly a useful predictor."
  },
];

// DIMENSION 5 — EVALUATION OF ARGUMENTS (8 items)
// Is the argument for or against a position Strong or Weak?
// A strong argument is both relevant and substantial.
export const EVALUATION_ITEMS = [
  {
    id:"ct33", dim:"EVL", section:"Evaluation",
    passage:"Issue: Should Indian companies be required to publish a pay equity audit annually?",
    q:"Argument: Yes, because pay transparency reduces gender and caste-based wage discrimination, which has been documented in multiple Indian labour market studies.",
    opts:["Strong Argument","Weak Argument"], ans:0,
    exp:"The argument is relevant to the issue and supported by research evidence — a strong argument."
  },
  {
    id:"ct34", dim:"EVL", section:"Evaluation",
    passage:"Issue: Should India raise the minimum age for company directorships from 18 to 25?",
    q:"Argument: No, because some of India's most successful startup founders became directors before the age of 25.",
    opts:["Strong Argument","Weak Argument"], ans:1,
    exp:"Exceptions to a rule do not determine policy. This is anecdotal reasoning and does not address the broader merits of the age threshold."
  },
  {
    id:"ct35", dim:"EVL", section:"Evaluation",
    passage:"Issue: Should law firms adopt billable-hour alternatives such as fixed-fee or value-based pricing?",
    q:"Argument: Yes, because billable-hour models incentivise inefficiency and are increasingly unpopular with corporate clients seeking cost predictability.",
    opts:["Strong Argument","Weak Argument"], ans:0,
    exp:"The argument addresses both the supply side (firm incentive misalignment) and demand side (client preference) — relevant and substantive."
  },
  {
    id:"ct36", dim:"EVL", section:"Evaluation",
    passage:"Issue: Should SEBI mandate ESG disclosures for all listed companies in India, not just the top 1,000?",
    q:"Argument: No, because smaller listed companies may lack the resources and expertise to produce credible ESG reports.",
    opts:["Strong Argument","Weak Argument"], ans:0,
    exp:"Implementation capacity is a legitimate constraint on regulatory design. This is a substantive, relevant concern."
  },
  {
    id:"ct37", dim:"EVL", section:"Evaluation",
    passage:"Issue: Should consulting firms prohibit partners from sitting on client company boards?",
    q:"Argument: Yes, because the firm's name sounds less credible when partners are seen to have personal financial interests in client decisions.",
    opts:["Strong Argument","Weak Argument"], ans:1,
    exp:"'Sounds less credible' is subjective and reputational rather than substantive. The real concern is conflicts of interest — this argument doesn't make it clearly."
  },
  {
    id:"ct38", dim:"EVL", section:"Evaluation",
    passage:"Issue: Should Indian banks be permitted to charge differential interest rates based on a borrower's geographic location?",
    q:"Argument: No, because this could entrench economic inequality between urban and rural borrowers who may already face higher financial exclusion.",
    opts:["Strong Argument","Weak Argument"], ans:0,
    exp:"The argument addresses a substantive equity concern directly relevant to banking policy and financial inclusion goals."
  },
  {
    id:"ct39", dim:"EVL", section:"Evaluation",
    passage:"Issue: Should India introduce a federal-level employment data registry tracking every formal sector worker?",
    q:"Argument: No, because the government already struggles to maintain the existing labour databases and adding another registry would be redundant.",
    opts:["Strong Argument","Weak Argument"], ans:1,
    exp:"Past failures in maintaining databases don't mean a new federal registry is unnecessary. The argument confuses implementation difficulty with policy merit."
  },
  {
    id:"ct40", dim:"EVL", section:"Evaluation",
    passage:"Issue: Should organisations use standardised psychometric tests as the primary shortlisting tool for senior management roles?",
    q:"Argument: No, because psychometric tests measure stable traits but senior roles require contextual judgement that tests cannot fully capture, making them insufficient as the sole criterion.",
    opts:["Strong Argument","Weak Argument"], ans:0,
    exp:"The argument makes a valid distinction between what tests measure and what senior roles demand — a substantive and relevant argument for using tests as one tool, not the only one."
  },
];

export const CT_ITEMS = [
  ...INFERENCE_ITEMS,
  ...ASSUMPTION_ITEMS,
  ...DEDUCTION_ITEMS,
  ...INTERPRETATION_ITEMS,
  ...EVALUATION_ITEMS,
];

export const CT_DIMS = [
  { id:"INF", label:"Inference",              color:"#1d4ed8", icon:"🔍",
    desc:"Evaluating whether conclusions drawn from facts are justified" },
  { id:"ASM", label:"Recognition of Assumptions", color:"#7c3aed", icon:"💭",
    desc:"Identifying unstated assumptions embedded in arguments" },
  { id:"DED", label:"Deduction",              color:"#0891b2", icon:"⚙️",
    desc:"Determining whether conclusions follow necessarily from premises" },
  { id:"INT", label:"Interpretation",         color:"#059669", icon:"📊",
    desc:"Deciding whether evidence justifies a given conclusion" },
  { id:"EVL", label:"Evaluation of Arguments",color:"#c8964e", icon:"⚖️",
    desc:"Distinguishing strong, relevant arguments from weak ones" },
];
